import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./PromptTemplateDetailPage.css";
import apiService from "../../../services/api";
import { normalizeTemplate } from "../../../components/settings/prompt-template/detail/utils";
import PageHeader from "../../../components/settings/prompt-template/detail/PageHeader";
import OverviewCard from "../../../components/settings/prompt-template/detail/OverviewCard";
import ContentCard from "../../../components/settings/prompt-template/detail/ContentCard";
import SchemaCard from "../../../components/settings/prompt-template/detail/SchemaCard";
import EditModal from "../../../components/settings/prompt-template/detail/EditModal";
import DeleteModal from "../../../components/common/DeleteModal";

function PromptTemplateDetailPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [editError, setEditError] = useState(null);

  const isSystem = template?.category === "system";

  const loadTemplate = useCallback(async () => {
    if (!templateId) {
      setError("Missing template identifier");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccessMessage("");
      const response = await apiService.getPromptTemplate(templateId);

      if (response?.success && response.data) {
        const normalized = normalizeTemplate(response.data, templateId);
        setTemplate(normalized);
      } else {
        setError(response?.message || "Failed to load prompt template");
      }
    } catch (err) {
      console.error("Error loading prompt template:", err);
      setError(err.response?.data?.message || "Failed to load prompt template");
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const parsedOutputSchema = useMemo(() => {
    if (!template?.outputSchema) {
      return null;
    }

    if (typeof template.outputSchema === "string") {
      return template.outputSchema;
    }

    try {
      return JSON.stringify(template.outputSchema, null, 2);
    } catch (error) {
      console.warn("Failed to stringify parsed output schema", error);
      return String(template.outputSchema);
    }
  }, [template]);

  const handleOpenEditModal = () => {
    if (!template) {
      return;
    }

    setEditError(null);
    setSuccessMessage("");
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditError(null);
  };

  const handleCancelEdit = () => {
    handleCloseEditModal();
    setSuccessMessage("");
  };

  const handleEditSuccess = async (normalized) => {
    if (normalized) {
      setTemplate(normalized);
    } else {
      await loadTemplate();
    }
    setSuccessMessage("Template updated successfully");
  };

  const handleDelete = () => {
    if (!template || isSystem) {
      return;
    }
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteError(null);
  };

  const handleConfirmDelete = async (confirmationCode) => {
    if (!template || isSystem) {
      return;
    }

    try {
      setSaving(true);
      setDeleteError(null);
      setError(null);
      setSuccessMessage("");

      const response = await apiService.deletePromptTemplate(template.id);

      if (response?.success) {
        setShowDeleteModal(false);
        navigate("/user-home/prompt-templates", { replace: true });
      } else {
        setDeleteError(response?.message || "Failed to delete prompt template");
      }
    } catch (err) {
      console.error("Error deleting prompt template:", err);
      setDeleteError(
        err.response?.data?.message || "Failed to delete prompt template"
      );
    } finally {
      setSaving(false);
    }
  };

  const pageActions = (
    <>
      <button
        type="button"
        className="user-home-layout__action-button user-home-layout__action-button--secondary prompt-template-detail__action-button"
        onClick={() => navigate("/user-home/prompt-templates")}
      >
        &larr; Back to Templates
      </button>
      <button
        type="button"
        className="user-home-layout__action-button user-home-layout__action-button--primary prompt-template-detail__action-button"
        onClick={showEditModal ? handleCloseEditModal : handleOpenEditModal}
        disabled={!template}
      >
        Edit Template
      </button>
    </>
  );

  return (
    <div className="user-home-layout__page prompt-template-detail-page">
      <PageHeader
        title={template?.name || "Prompt Template"}
        subtitle={
          isSystem
            ? "System-level prompt. Edit to customize system-wide behavior."
            : "Review template details, update content, and manage its lifecycle."
        }
        actions={pageActions}
      />

      {loading ? (
        <div className="prompt-template-detail__status-card">
          <p className="prompt-template-detail__status-text">
            Loading template...
          </p>
        </div>
      ) : (
        <>
          {!showEditModal && error && (
            <div className="prompt-template-detail__alert prompt-template-detail__alert--error">
              {error}
            </div>
          )}
          {!showEditModal && successMessage && (
            <div className="prompt-template-detail__alert prompt-template-detail__alert--success">
              {successMessage}
            </div>
          )}

          {template ? (
            <div className="prompt-template-detail__grid">
              <OverviewCard
                template={template}
                onRefresh={loadTemplate}
                onDelete={handleDelete}
                loading={loading}
                saving={saving}
              />
              <ContentCard content={template.content} />
              <SchemaCard outputSchema={parsedOutputSchema} />
            </div>
          ) : (
            <div className="prompt-template-detail__status-card">
              <p className="prompt-template-detail__status-text">
                The requested prompt template could not be found.
              </p>
            </div>
          )}
        </>
      )}

      <EditModal
        show={showEditModal && !!template}
        template={template}
        error={editError}
        onCancel={handleCancelEdit}
        onError={setEditError}
        onSuccess={handleEditSuccess}
        onClose={handleCloseEditModal}
      />

      {!isSystem && (
        <DeleteModal
          isOpen={showDeleteModal}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          itemName={template?.name || "this prompt template"}
          title="Delete Prompt Template"
          confirmButtonText="Delete"
          error={deleteError}
          entityType="prompt template"
        />
      )}
    </div>
  );
}

export default PromptTemplateDetailPage;
